import React, { useState } from "react";
import { Box, Grid, Typography, Button, Chip, Container } from "@mui/material";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const sapIcons = ["FI", "CO", "MM", "SD", "PP", "QM", "ABAP", "HANA"];

const HeroSection = ({ scrollToCapabilities }) => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    setMouse({
      x: (e.clientX - innerWidth / 2) / 35,
      y: (e.clientY - innerHeight / 2) / 35,
    });
  };

  return (
    <Box
      onMouseMove={handleMouseMove}
      sx={{
        minHeight: { xs: "100vh", md: "calc(100vh - 80px)" },
        background: "linear-gradient(135deg, #071525 0%, #082d63 100%)",
        color: "#fff",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        px: { xs: 2, sm: 4, md: 10 },
        py: { xs: 8, md: 10 },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        {/* Background particles */}
        {[...Array(28)].map((_, i) => (
          <MotionBox
            key={i}
            animate={{
              y: [0, -35, 0],
              opacity: [0.25, 0.8, 0.25],
            }}
            transition={{
              duration: 4 + i * 0.15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            sx={{
              position: "absolute",
              width: { xs: 3, md: 5 },
              height: { xs: 3, md: 5 },
              borderRadius: "50%",
              background: i % 3 === 0 ? "#00c853" : "#2f80ff",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              opacity: 0.35,
            }}
          />
        ))}

        <Grid container spacing={6} alignItems="center" sx={{ zIndex: 2 }}>
          {/* LEFT CONTENT */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <MotionTypography
              initial={{ opacity: 0, y: 45 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              sx={{
                fontSize: { xs: "2rem", sm: "2.4rem", md: "3rem" },
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: "-2px",
              }}
            >
              We Optimize Your
              <br />
              Business With
            </MotionTypography>

            <MotionTypography
              initial={{ opacity: 0, y: 45 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              viewport={{ once: true }}
              sx={{
                fontSize: { xs: "2rem", sm: "2.4rem", md: "3rem" },
                fontWeight: 800,
                lineHeight: 1.05,
                mt: 1,
                background: "linear-gradient(90deg, #00c853, #2f80ff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              SAP Solutions
            </MotionTypography>

            <MotionTypography
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              viewport={{ once: true }}
              sx={{
                mt: 4,
                maxWidth: 580,
                fontSize: { xs: "0.8rem", md: "1rem" },
                lineHeight: 1.5,
                color: "rgba(255,255,255,0.82)",
                textAlign: "justify",
              }}
            >
              ZENCON helps companies streamline operations, automate workflows,
              improve reporting, and achieve better performance through SAP
              consulting, implementation, support, and process optimization.
            </MotionTypography>

            <MotionBox
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              viewport={{ once: true }}
            >
              <Button
                variant="contained"
                sx={{
                  mt: 4,
                  px: 4,
                  py: 1.4,
                  borderRadius: "30px",
                  textTransform: "none",
                  fontWeight: 700,
                  background: "#00c853",
                  boxShadow: "none",
                  "&:hover": {
                    background: "#00b84d",
                    boxShadow: "0 10px 30px rgba(0,200,83,0.35)",
                    transform: "translateY(-2px)",
                  },
                  transition: "0.3s ease",
                }}
                onClick={scrollToCapabilities}
              >
                Explore Services
              </Button>
            </MotionBox>
          </Grid>

          {/* RIGHT GRAPHICS */}
          <Grid item xs={12} md={6}>
            <MotionBox
              initial={{ opacity: 0, scale: 0.85, y: 40 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              sx={{
                position: "relative",
                width: { xs: 330, sm: 360, md: 470 },
                height: { xs: 330, sm: 360, md: 470 },
                mx: "auto",
                transform: {
                  xs: "none",
                  md: `translate(${mouse.x}px, ${mouse.y}px)`,
                },
                transition: "transform 0.15s ease-out",
              }}
            >
              {/* Outer rotating ring */}
              <MotionBox
                animate={{ rotate: 360 }}
                transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                sx={{
                  position: "absolute",
                  inset: "10%",
                  border: "5px solid rgba(255,255,255,0.85)",
                  borderRadius: "50%",
                }}
              />

              {/* Inner dashboard screen */}
              <Box
                sx={{
                  position: "absolute",
                  inset: "20%",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))",
                  backdropFilter: "blur(14px)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 35px 90px rgba(0,0,0,0.38)",
                }}
              >
                <Box
                  sx={{
                    width: "78%",
                    height: "58%",
                    borderRadius: "20px",
                    background: "rgba(7,21,37,0.96)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    p: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#ff3860",
                      }}
                    />
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#ffcc00",
                      }}
                    />
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#00c853",
                      }}
                    />
                  </Box>

                  {[
                    "SAP Integration",
                    "Workflow Automation",
                    "Smart Reports",
                  ].map((item, i) => (
                    <MotionBox
                      key={item}
                      animate={{
                        width: [
                          `${55 + i * 10}%`,
                          `${90 - i * 10}%`,
                          `${55 + i * 10}%`,
                        ],
                      }}
                      transition={{
                        duration: 3 + i,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      sx={{
                        height: 11,
                        borderRadius: 8,
                        mb: 1.5,
                        background:
                          "linear-gradient(90deg, #00c853, rgba(47,128,255,0.9))",
                      }}
                    />
                  ))}

                  <Box
                    sx={{
                      mt: 3,
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.4,
                    }}
                  >
                    {[1, 2, 3, 4].map((i) => (
                      <MotionBox
                        key={i}
                        animate={{ opacity: [0.45, 1, 0.45] }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          delay: i * 0.25,
                        }}
                        sx={{
                          height: 36,
                          borderRadius: 2,
                          background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Floating SAP module chips */}
              {sapIcons.map((icon, i) => (
                <MotionBox
                  key={icon}
                  animate={{
                    y: [0, -18, 0],
                    rotate: [0, 4, -4, 0],
                  }}
                  transition={{
                    duration: 3 + i * 0.25,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  sx={{
                    position: "absolute",
                    top: `${12 + (i % 4) * 18}%`,
                    left: i % 2 === 0 ? `${2 + i * 4}%` : "auto",
                    right: i % 2 !== 0 ? `${4 + i * 3}%` : "auto",
                  }}
                >
                  <Chip
                    label={icon}
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, rgba(0,200,83,0.9), rgba(47,128,255,0.9))",
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                    }}
                  />
                </MotionBox>
              ))}

              {/* Decorative shapes */}
              <MotionBox
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                sx={{
                  position: "absolute",
                  top: "8%",
                  right: "12%",
                  width: 82,
                  height: 82,
                  borderRadius: "50%",
                  border: "8px solid #00c853",
                }}
              />

              <MotionBox
                animate={{ rotate: 360 }}
                transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
                sx={{
                  position: "absolute",
                  right: "4%",
                  bottom: "30%",
                  width: 72,
                  height: 72,
                  border: "5px solid #ff2f6d",
                  borderRadius: "10px",
                }}
              />

              <MotionBox
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 3.5, repeat: Infinity }}
                sx={{
                  position: "absolute",
                  left: "12%",
                  bottom: "25%",
                  width: 0,
                  height: 0,
                  borderTop: "18px solid transparent",
                  borderBottom: "18px solid transparent",
                  borderLeft: "28px solid white",
                }}
              />
            </MotionBox>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
